/**
 * A game's public rules page, for game types that have one: publish (or
 * unpublish) it at games.swtorbit.com/game/ID/CODE, copy or open its link, set
 * the scheduled start and the description it shows, and see where /game has
 * posted it in Discord. Each round's own display settings are in its form.
 */

import { useEffect, useState } from 'react';
import { Alert, Badge, Field, Input, Textarea } from '@swtorbit/ui/react';
import { GAME_PUBLIC_DESCRIPTION_MAX, type Game } from '@swtorbit/shared-socket-events';
import { useGameRules } from '../../hooks/useGameRules';
import { browserTimezone, getAdminProfile } from '../../services/adminProfileApi';
import { utcToZonedLocal, zonedLocalToUtc } from '../../utils/zonedTime';

function when(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** The zone the start is entered in: the admin's profile's, else the browser's. */
function useAdminTimeZone(): string {
  const [timeZone, setTimeZone] = useState(() => browserTimezone() ?? 'UTC');
  useEffect(() => {
    let live = true;
    getAdminProfile()
      .then((profile) => live && profile.timezone && setTimeZone(profile.timezone))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return timeZone;
}

export function RulesPanel({ game }: { game: Game }) {
  const rules = useGameRules(game);
  const { status, loading, error } = rules;
  const timeZone = useAdminTimeZone();
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [start, setStart] = useState('');
  const [description, setDescription] = useState('');
  const [saved, setSaved] = useState(false);

  // The form follows the saved values (and the zone, once the profile has loaded).
  const savedStart = status?.scheduledStartAtUtcMs ?? null;
  const savedDescription = status?.description ?? '';
  useEffect(() => {
    setStart(savedStart === null ? '' : utcToZonedLocal(savedStart, timeZone));
    setDescription(savedDescription);
  }, [savedStart, savedDescription, timeZone]);

  if (!status || !status.supported) return null;

  const published = status.publishedAtUtcMs !== null;
  const startUtc = start ? zonedLocalToUtc(start, timeZone) : null;
  const changed = startUtc !== savedStart || description.trim() !== savedDescription;

  /** Copy a link; `which` says which one shows "Copied" (the public page, or a team's). */
  const copy = async (url: string | null, which: string) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      setCopied(null);
    }
  };

  const save = async () => {
    if (start && startUtc === null) return;
    if (await rules.updateInfo(startUtc, description.trim() || null)) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  return (
    <section className="card mb-base">
      <div className="card__body d-flex flex-column gap-sm">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-sm">
          <h3 className="text-white m-0">Rules page</h3>
          <Badge hue={published ? 'green' : 'slate'}>{published ? 'Published' : 'Not published'}</Badge>
        </div>

        <p className="fs-sm text-slate m-0">
          A public page of this game&apos;s rounds and rules. Run <code>/game</code> in your Discord server to post it there; the
          post follows the rounds as you change them. What each round shows is set in its form.
        </p>

        {status.publicUrl && (
          <div className="d-flex flex-wrap align-items-center gap-xs">
            <code className={published ? 'text-cyan' : 'text-slate'}>{status.publicUrl}</code>
            <button className="btn btn--outline btn--slate btn--xs" onClick={() => void copy(status.publicUrl, 'public')}>
              {copied === 'public' ? 'Copied' : 'Copy'}
            </button>
            {published && (
              <a className="btn btn--outline btn--cyan btn--xs" href={status.publicUrl} target="_blank" rel="noreferrer">
                Open
              </a>
            )}
          </div>
        )}
        {published && (
          <span className="fs-xs text-slate">
            Published {when(status.publishedAtUtcMs!)}
            {status.publishedBy ? ` by ${status.publishedBy}` : ''}
          </span>
        )}

        {published && status.teamPages.length > 0 && (
          <div className="d-flex flex-column gap-2xs">
            <span className="fs-xs text-slate text-uppercase fw-bold">Team pages (secret)</span>
            <p className="fs-xs text-slate m-0">
              Each team&apos;s own page also shows its Team Captain picks before they are revealed. Give a team only its own link, or
              run <code>/game team:</code> in its private channel.
            </p>
            {status.teamPages.map((page) => (
              <div key={page.team} className="d-flex flex-wrap align-items-center gap-xs">
                <span className="fw-bold">{page.name}</span>
                <code className="text-cyan">{page.url}</code>
                <button className="btn btn--outline btn--slate btn--xs" onClick={() => void copy(page.url, page.team)}>
                  {copied === page.team ? 'Copied' : 'Copy'}
                </button>
                <a className="btn btn--outline btn--cyan btn--xs" href={page.url} target="_blank" rel="noreferrer">
                  Open
                </a>
              </div>
            ))}
          </div>
        )}

        <div className="d-flex flex-wrap gap-xs">
          {!published && (
            <button className="btn btn--green btn--sm" disabled={loading} onClick={() => void rules.publish()}>
              Publish rules page
            </button>
          )}
          {published && !confirmUnpublish && (
            <button className="btn btn--outline btn--red btn--sm" disabled={loading} onClick={() => setConfirmUnpublish(true)}>
              Unpublish
            </button>
          )}
          {published && confirmUnpublish && (
            <>
              <button
                className="btn btn--red btn--sm"
                disabled={loading}
                onClick={() => {
                  setConfirmUnpublish(false);
                  void rules.unpublish();
                }}
              >
                Yes, take the page down
              </button>
              <button className="btn btn--outline btn--slate btn--sm" onClick={() => setConfirmUnpublish(false)}>
                Keep it
              </button>
            </>
          )}
        </div>
        {confirmUnpublish && (
          <p className="fs-xs text-slate m-0">Posts already in Discord stay, but stop updating and their link stops working.</p>
        )}

        <Field label="Scheduled start" hint={`Your time zone: ${timeZone}. Shown to everyone in their own time.`}>
          <div className="d-flex flex-wrap gap-xs align-items-center">
            <Input type="datetime-local" size="sm" value={start} onChange={(e) => setStart(e.target.value)} />
            {start && (
              <button className="btn btn--outline btn--slate btn--xs" onClick={() => setStart('')}>
                Clear
              </button>
            )}
          </div>
        </Field>

        <Field label="Description" hint={`Shown under the logo. Up to ${GAME_PUBLIC_DESCRIPTION_MAX} characters.`}>
          <Textarea
            rows={3}
            maxLength={GAME_PUBLIC_DESCRIPTION_MAX}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What players should know about this game"
          />
        </Field>

        <div className="d-flex align-items-center gap-sm">
          <button className="btn btn--cyan btn--sm" disabled={loading || !changed || (Boolean(start) && startUtc === null)} onClick={() => void save()}>
            Save
          </button>
          {saved && <span className="fs-sm text-green">Saved</span>}
        </div>

        {status.posts.length > 0 && (
          <div className="d-flex flex-column gap-2xs">
            <span className="fs-xs text-slate text-uppercase fw-bold">Posted in Discord</span>
            {status.posts.map((post) => (
              <div key={`${post.guildId}:${post.channelId}`} className="d-flex flex-wrap align-items-center gap-xs fs-sm">
                <a href={post.messageUrl} target="_blank" rel="noreferrer">
                  Open in Discord
                </a>
                <span className="text-slate">
                  {post.audience ? `${status.teamPages.find((p) => p.team === post.audience)?.name ?? post.audience} team view, ` : ''}
                  {post.messages} message{post.messages === 1 ? '' : 's'}, updated {when(post.updatedAtUtcMs)}
                  {post.postedBy ? `, posted by ${post.postedBy}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {error && <Alert hue="red">{error}</Alert>}
      </div>
    </section>
  );
}
